// DataTable — full-featured table with search, filter, sort, pagination, time range, export, saved views
import { useState } from 'react';
import type { Column, SortDir, TimeRange, SavedView } from '../hooks/useTableControls';

interface DataTableProps<T> {
  columns: Column<T>[];
  visibleColumns: Column<T>[];
  data: T[];
  search: string;
  onSearchChange: (v: string) => void;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
  page: number;
  totalPages: number;
  totalFiltered: number;
  pageSize: number;
  onPageChange: (v: number) => void;
  onPageSizeChange: (v: number) => void;
  activeFilters: Record<string, string>;
  onFilter: (key: string, value: string) => void;
  onClearFilters: () => void;
  searchPlaceholder?: string;
  keyAccessor: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  // New features
  timeRange: TimeRange | null;
  onTimeRangeChange: (tr: TimeRange | null) => void;
  hiddenColumns: string[];
  onToggleColumn: (key: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  savedViews: SavedView[];
  onSaveView: (name: string) => void;
  onLoadView: (id: string) => void;
  onDeleteView: (id: string) => void;
  onExportCSV: () => void;
  onExportExcel: () => void;
  showSelection?: boolean;
}

function SortIndicator() {
  return (
    <span className="th__ind">
      <svg viewBox="0 0 8 5"><path d="M1 4l3-3 3 3" /></svg>
      <svg viewBox="0 0 8 5"><path d="M1 1l3 3 3-3" /></svg>
    </span>
  );
}

const TIME_PRESETS = [
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 7d', hours: 168 },
  { label: 'Last 30d', hours: 720 },
  { label: 'Last 90d', hours: 2160 },
  { label: 'Last 1y', hours: 8760 },
];

export function DataTable<T extends Record<string, any>>({
  columns, visibleColumns, data,
  search, onSearchChange,
  sortKey, sortDir, onSort,
  page, totalPages, totalFiltered, pageSize,
  onPageChange, onPageSizeChange,
  activeFilters, onFilter, onClearFilters,
  searchPlaceholder = 'Search...',
  keyAccessor, onRowClick, emptyMessage = 'No data',
  timeRange, onTimeRangeChange,
  hiddenColumns, onToggleColumn,
  selectedIds, onToggleSelect, onSelectAll, onClearSelection,
  savedViews, onSaveView, onLoadView, onDeleteView,
  onExportCSV, onExportExcel,
  showSelection = false,
}: DataTableProps<T>) {
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showTimeRange, setShowTimeRange] = useState(false);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const hasActiveFilters = Object.keys(activeFilters).length > 0 || search.length > 0 || timeRange != null;
  const dateColumns = columns.filter(c => c.dateField);
  const hasTimeRange = dateColumns.length > 0;

  const applyTimePreset = (hours: number) => {
    const end = new Date().toISOString();
    const start = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    const field = dateColumns[0]?.key || 'last_seen_at';
    onTimeRangeChange({ field, start, end });
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    const field = dateColumns[0]?.key || 'last_seen_at';
    onTimeRangeChange({
      field,
      start: new Date(customStart).toISOString(),
      end: new Date(customEnd).toISOString(),
    });
    setShowTimeRange(false);
  };

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    onSaveView(newViewName.trim());
    setNewViewName('');
    setShowSavedViews(false);
  };

  return (
    <div>
      {/* Controls bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        {/* Search */}
        <div className="filter-group" style={{ flex: '1 1 250px' }}>
          <label className="filter-label">Search</label>
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            style={{
              padding: '6px 12px', border: '1px solid var(--hair-ctl)', borderRadius: 'var(--radius-sm)',
              fontSize: 13, width: '100%', background: 'var(--surface)',
            }}
          />
        </div>

        {/* Column filters */}
        {columns.filter(c => c.filterOptions).map(col => (
          <div className="filter-group" key={col.key}>
            <label className="filter-label">{col.label}</label>
            <select
              className="filter-select"
              value={activeFilters[col.key] || ''}
              onChange={e => onFilter(col.key, e.target.value)}
            >
              <option value="">All</option>
              {col.filterOptions!.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ))}

        {/* Time range */}
        {hasTimeRange && (
          <div className="filter-group" style={{ position: 'relative' }}>
            <label className="filter-label">Time Range</label>
            <button
              className="btn btn--primary"
              onClick={() => setShowTimeRange(!showTimeRange)}
              style={{ fontSize: 12, padding: '5px 12px', borderColor: timeRange ? 'var(--gold)' : undefined }}
            >
              {timeRange ? '🕐 Filtered' : '🕐 Time Range'}
            </button>
            {showTimeRange && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 50,
                background: 'var(--surface)', border: '1px solid var(--hair-ctl)',
                borderRadius: 'var(--radius-md)', padding: 16, minWidth: 280,
                boxShadow: 'var(--shadow-lg)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Quick Presets</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                  {TIME_PRESETS.map(p => (
                    <button
                      key={p.hours}
                      className="btn btn--primary"
                      style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={() => { applyTimePreset(p.hours); setShowTimeRange(false); }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Custom Range</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="datetime-local"
                    value={customStart}
                    onChange={e => setCustomStart(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--hair-ctl)', borderRadius: 4, flex: 1 }}
                  />
                  <input
                    type="datetime-local"
                    value={customEnd}
                    onChange={e => setCustomEnd(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--hair-ctl)', borderRadius: 4, flex: 1 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn--primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={applyCustomRange}>
                    Apply
                  </button>
                  {timeRange && (
                    <button className="btn btn--primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { onTimeRangeChange(null); setShowTimeRange(false); }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Page size */}
        <div className="filter-group">
          <label className="filter-label">Rows</label>
          <select
            className="filter-select"
            value={pageSize}
            onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Column visibility */}
        <div className="filter-group" style={{ position: 'relative' }}>
          <label className="filter-label">Columns</label>
          <button
            className="btn btn--primary"
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            style={{ fontSize: 12, padding: '5px 12px' }}
          >
            ☰ {visibleColumns.length}/{columns.length}
          </button>
          {showColumnPicker && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 50,
              background: 'var(--surface)', border: '1px solid var(--hair-ctl)',
              borderRadius: 'var(--radius-md)', padding: 12, minWidth: 200,
              boxShadow: 'var(--shadow-lg)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Toggle Columns</div>
              {columns.filter(c => c.hideable !== false).map(col => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.includes(col.key)}
                    onChange={() => onToggleColumn(col.key)}
                    style={{ width: 14, height: 14 }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Saved views */}
        <div className="filter-group" style={{ position: 'relative' }}>
          <label className="filter-label">Views</label>
          <button
            className="btn btn--primary"
            onClick={() => setShowSavedViews(!showSavedViews)}
            style={{ fontSize: 12, padding: '5px 12px' }}
          >
            ★ Views {savedViews.length > 0 && `(${savedViews.length})`}
          </button>
          {showSavedViews && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 50,
              background: 'var(--surface)', border: '1px solid var(--hair-ctl)',
              borderRadius: 'var(--radius-md)', padding: 12, minWidth: 240,
              boxShadow: 'var(--shadow-lg)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Save Current View</div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                <input
                  type="text"
                  value={newViewName}
                  onChange={e => setNewViewName(e.target.value)}
                  placeholder="View name..."
                  style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--hair-ctl)', borderRadius: 4, flex: 1 }}
                  onKeyDown={e => e.key === 'Enter' && handleSaveView()}
                />
                <button className="btn btn--primary" style={{ fontSize: 12, padding: '4px 8px' }} onClick={handleSaveView}>
                  Save
                </button>
              </div>
              {savedViews.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Saved Views</div>
                  {savedViews.map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--gold)', textAlign: 'left' }}
                        onClick={() => { onLoadView(v.id); setShowSavedViews(false); }}
                      >
                        {v.name}
                      </button>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--risk)', padding: '2px 4px' }}
                        onClick={() => onDeleteView(v.id)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Export */}
        <div className="filter-group">
          <label className="filter-label">Export</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn--primary" onClick={onExportCSV} style={{ fontSize: 12, padding: '5px 8px' }} title="Export CSV">
              CSV
            </button>
            <button className="btn btn--primary" onClick={onExportExcel} style={{ fontSize: 12, padding: '5px 8px' }} title="Export Excel">
              Excel
            </button>
          </div>
        </div>

        {/* Clear */}
        {hasActiveFilters && (
          <button className="btn btn--primary" onClick={onClearFilters} style={{ marginBottom: 0 }}>
            Clear all
          </button>
        )}
      </div>

      {/* Active time range indicator */}
      {timeRange && (
        <div style={{ fontSize: 12, color: 'var(--gold)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🕐 Showing data from {new Date(timeRange.start).toLocaleDateString()} to {new Date(timeRange.end).toLocaleDateString()}</span>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--risk)' }}
            onClick={() => onTimeRangeChange(null)}
          >
            ✕ Clear time filter
          </button>
        </div>
      )}

      {/* Results count + selection */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--nv2)', marginBottom: 8 }}>
        <span>Showing {data.length} of {totalFiltered} results</span>
        {showSelection && selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{selectedIds.size} selected</span>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--nv2)' }} onClick={onClearSelection}>
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="tbl-scroll">
        <table className="tbl">
          <thead>
            <tr>
              {showSelection && (
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    onChange={e => e.target.checked ? onSelectAll() : onClearSelection()}
                    checked={data.length > 0 && data.every(row => selectedIds.has(keyAccessor(row)))}
                    style={{ width: 14, height: 14 }}
                  />
                </th>
              )}
              {visibleColumns.map(col => (
                <th key={col.key}>
                  {col.sortable !== false ? (
                    <button
                      className="th"
                      aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none') : 'none'}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                      <SortIndicator />
                    </button>
                  ) : (
                    <span className="th th--static">{col.label}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (showSelection ? 1 : 0)} style={{ textAlign: 'center', padding: 32 }}>
                  <span className="text-muted">{emptyMessage}</span>
                </td>
              </tr>
            ) : (
              data.map(row => {
                const id = keyAccessor(row);
                return (
                  <tr
                    key={id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    aria-selected={selectedIds.has(id)}
                    style={{ cursor: onRowClick ? 'pointer' : undefined }}
                  >
                    {showSelection && (
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(id)}
                          onChange={() => onToggleSelect(id)}
                          style={{ width: 14, height: 14 }}
                        />
                      </td>
                    )}
                    {visibleColumns.map(col => (
                      <td key={col.key}>
                        {col.render ? col.render(row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
          <span className="text-muted">Page {page} of {totalPages}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn--primary" disabled={page <= 1} onClick={() => onPageChange(1)} style={{ padding: '4px 8px', fontSize: 12 }}>⟪</button>
            <button className="btn btn--primary" disabled={page <= 1} onClick={() => onPageChange(page - 1)} style={{ padding: '4px 8px', fontSize: 12 }}>← Prev</button>
            <button className="btn btn--primary" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} style={{ padding: '4px 8px', fontSize: 12 }}>Next →</button>
            <button className="btn btn--primary" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} style={{ padding: '4px 8px', fontSize: 12 }}>⟫</button>
          </div>
        </div>
      )}
    </div>
  );
}
